import { useEffect, useRef } from 'react';
import { Analytics, getPlatformAndVersion } from './analytics';
import * as ls from './localStorage';
import { roles } from './constants';

export type SignupPage = 'create_account' | 'talent_your_info' | 'talent_profile'
    | 'talent_route_choice' | 'office_info' | 'office_details' | 'office_route_choice';
export type SignupUserType = 'talent' | 'office' | 'unknown';
type Details = { role?: string | number; route_selected?: string; errorReason?: string;
    fromUserType?: SignupUserType; toUserType?: SignupUserType };

export function signupRole(role?: string | number): string | undefined {
    const roleId = String(role ?? '');
    if (roleId === String(roles.HYGIENIST) || roleId === 'Hygienist') return 'Hygienist';
    if (roleId === String(roles.ASSISTANT) || roleId === 'Assistant') return 'Assistant';
    if (roleId === String(roles.FRONT_DESK) || ['Admin', 'Front Desc'].includes(roleId)) return 'Admin';
    if (roleId === String(roles.ASSOCIATE_DENTIST) || roleId === 'Associate Dentist') return 'Associate Dentist';
    return undefined;
}

export function signupErrorReason(error: unknown): string {
    // Never copy server text, email, phone, address or form values into analytics.
    let value: any = error;
    try { if (typeof value === 'string') value = JSON.parse(value); } catch (_) {}
    try { if (typeof value?.message === 'string' && value.message.startsWith('{')) value = JSON.parse(value.message); } catch (_) {}
    const code = value?.body?.error?.code || value?.error?.code || value?.code;
    if (code === 'USER_ALREADY_EXISTS' || value?.error?.message === 'Duplicate'
        || value?.body?.error?.isDuplicate === 1
        || value?.message === 'User with this email already exists') return 'duplicate_account';
    if (code === 'INVALID_REFERRAL_CODE') return 'invalid_referral_code';
    if (code === 'EXPIRED_REFERRAL_CODE') return 'expired_referral_code';
    return 'save_failed';
}

export function trackSignup(page: SignupPage, userType: SignupUserType,
    action: 'viewed' | 'submitted' | 'error' | 'duplicate account' | 'switched role', details: Details = {}) {
    try {
        const account = ls.get('userAccount');
        const storedRole = account?.roleId ?? (account?.hygienist?.type == null ? undefined
            : [roles.HYGIENIST, roles.ASSISTANT, roles.FRONT_DESK, roles.ASSOCIATE_DENTIST][Number(account.hygienist.type)]);
        const role = userType === 'talent' ? signupRole(details.role ?? storedRole) : undefined;
        const { role: ignoredRole, ...properties } = details;
        const name = action === 'duplicate account' || action === 'switched role'
            ? `sign-up - ${action}` : `sign-up - page ${action}`;
        Analytics.track(name, { page, userType, platform: getPlatformAndVersion().platform,
            ...(role ? { role } : {}), ...properties });
    } catch (_) { /* Optional telemetry must never interrupt signup or navigation. */ }
}

export function useSignupPage(page: SignupPage, userType: SignupUserType, role?: string | number, viewEnabled = true) {
    const viewed = useRef(false);
    const routePendingOrSaved = useRef(false);
    const selectedRole = useRef(role);
    const previousRole = useRef(role);
    if (role !== undefined && role !== previousRole.current) selectedRole.current = role;
    previousRole.current = role;
    useEffect(() => {
        if (viewEnabled && !viewed.current) { viewed.current = true; trackSignup(page, userType, 'viewed', { role }); }
    }, [page, userType, role, viewEnabled]);
    return {
        setRole: (value: string | number) => { selectedRole.current = value; },
        submitted: (details: Details = {}) => trackSignup(page, userType, 'submitted', { role: selectedRole.current, ...details }),
        error: (errorReason = 'save_failed') => trackSignup(page, userType, 'error', { role: selectedRole.current, errorReason }),
        duplicate: () => trackSignup(page, userType, 'duplicate account', { role: selectedRole.current }),
        switched: (toUserType: SignupUserType) => trackSignup(page, userType, 'switched role',
            { role: selectedRole.current, fromUserType: userType, toUserType }),
        route: async (route_selected: string, navigate: () => unknown) => {
            if (routePendingOrSaved.current) return;
            routePendingOrSaved.current = true;
            try {
                await navigate();
                trackSignup(page, userType, 'submitted', { role, route_selected });
            } catch (_) {
                routePendingOrSaved.current = false;
                trackSignup(page, userType, 'error', { role, errorReason: 'route_navigation_failed' });
            }
        },
    };
}
